import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Mail, Eye, Edit2, Trash2, Loader2, RefreshCw, BookmarkPlusIcon } from 'lucide-react';
import { PreviewModal } from '../components/PreviewModal';
import { EmailPreview } from '../components/EmailPreview';
import { emailTemplatesApi, EmailTemplateRecord, formConfigsApi, extractLookupId, emailTemplateLibraryApi } from '../services/creatorApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { auditService } from '../services/auditService';
import { toast } from 'sonner';
export function EmailTemplates() {
  const navigate = useNavigate();
  const {
    currentWorkspace,
    currentSubRegion,
    currentUser
  } = useWorkspace();
  const team = currentSubRegion || currentWorkspace.name.replace(/\s*Team\s*$/i, '');
  const [templates, setTemplates] = useState<EmailTemplateRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [previewTemplate, setPreviewTemplate] = useState<EmailTemplateRecord | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [savingAsTemplate, setSavingAsTemplate] = useState<string | null>(null);
  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const data = await emailTemplatesApi.list();
      setTemplates(data);
    } catch (err) {
      console.error('Failed to fetch email templates:', err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchTemplates();
  }, []);
  const handleSaveAsTemplate = async (templateId: string) => {
    setSavingAsTemplate(templateId);
    try {
      const tpl = await emailTemplatesApi.get(templateId);
      if (!tpl) {
        toast.error('Could not load template data');
        return;
      }
      await emailTemplateLibraryApi.create({
        Template_Name: (tpl.Email_Subject || 'Untitled') + ' (Template)',
        Email_HTML_Body: tpl.Email_HTML_Body || '',
        Email_Subject: tpl.Email_Subject || '',
        Preheader_Text: tpl.Preheader_Text || '',
        Builder_Schema: (tpl as any).Builder_Schema || '',
        Builder_Mode: (tpl as any).Builder_Mode || 'code',
        Team: team,
        Region: tpl.Region || currentSubRegion || ''
      });
      toast.success('Saved as email template', {
        description: `"${tpl.Email_Subject}" saved to the template library.`,
        action: {
          label: 'View Library',
          onClick: () => navigate('/email-templates-library')
        },
        duration: 5000
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to save as template', {
        description: msg
      });
    } finally {
      setSavingAsTemplate(null);
    }
  };
  const handleDelete = async (id: string) => {
    setIsDeleting(true);
    try {
      try {
        const allConfigs = await formConfigsApi.list();
        const linkedConfigs = allConfigs.filter(c => {
          const templateId = extractLookupId(c.Email_Template);
          return templateId === id;
        });
        for (const config of linkedConfigs) {
          await formConfigsApi.delete(config.ID);
        }
        if (linkedConfigs.length > 0) {
          toast.info(`Removed ${linkedConfigs.length} linked configuration(s)`);
        }
      } catch (cleanupErr) {
        console.warn('Failed to clean up linked configurations:', cleanupErr);
      }
      await emailTemplatesApi.delete(id);
      setTemplates(prev => prev.filter(t => t.ID !== id));
      setDeleteConfirm(null);
      // Fire-and-forget: Audit log on delete
      const deletedTemplate = templates.find(t => t.ID === id);
      auditService.log({
        actionType: 'deleted',
        entityType: 'template',
        entityId: id,
        entityName: deletedTemplate?.Email_Subject || id,
        userName: currentUser.User_Name,
        userEmail: currentUser.Email,
        userRole: currentUser.Role,
        team: currentWorkspace.name,
        region: currentSubRegion || ''
      });
      toast.success('Email template deleted');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to delete template', {
        description: msg
      });
    } finally {
      setIsDeleting(false);
    }
  };
  // Filter by team and region
  const filteredTemplates = templates.filter(t => {
    if (t.Team !== currentWorkspace.name && t.Team !== team) return false;
    if (currentSubRegion) {
      return !t.Region || t.Region === currentSubRegion;
    }
    return true;
  });
  return <div className="space-y-6" data-id="element-4042">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-4043">
        <div className="min-w-0" data-id="element-4044">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4045">
            Email Templates
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-4046">
            Manage double opt-in and confirmation emails stored in Zoho Creator.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0" data-id="element-4047">
          <button onClick={fetchTemplates} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" title="Refresh" data-id="element-4048">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-4049" />
          </button>
          <Link to="/templates/new" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 whitespace-nowrap" data-id="element-4050">
            <Plus className="h-4 w-4" data-id="element-4051" />
            <span data-id="element-4052">Create Template</span>
          </Link>
        </div>
      </div>

      {loading && templates.length === 0 ? <div className="bg-white border border-gray-200 rounded-lg p-12 text-center" data-id="element-4053">
          <Loader2 className="mx-auto h-8 w-8 text-blue-500 animate-spin" data-id="element-4054" />
          <p className="mt-2 text-sm text-gray-500" data-id="element-4055">
            Loading email templates...
          </p>
        </div> : filteredTemplates.length === 0 ? <div className="bg-white border border-gray-200 rounded-lg p-12 text-center" data-id="element-4056">
          <Mail className="mx-auto h-12 w-12 text-gray-400" data-id="element-4057" />
          <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-4058">
            No templates found
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-4059">
            Create your first email template to get started.
          </p>
          <Link to="/templates/new" className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-4060">
            <Plus className="h-4 w-4" data-id="element-4061" />
            Create First Template
          </Link>
        </div> : <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6" data-id="element-4062">
          {filteredTemplates.map(template => <div key={template.ID} className="bg-white border border-gray-200 rounded-lg p-4 lg:p-6 flex flex-col" data-id="element-4063">
              <div className="flex items-start justify-between mb-4 gap-3" data-id="element-4064">
                <div className="p-2 bg-blue-50 rounded-lg flex-shrink-0" data-id="element-4065">
                  <Mail className="h-5 w-5 lg:h-6 lg:w-6 text-blue-600" data-id="element-4066" />
                </div>
                {template.Team && <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600" data-id="element-4067">
                    {template.Team}
                  </span>}
              </div>
              <h3 className="text-base lg:text-lg font-medium text-gray-900 mb-1 truncate" data-id="element-4068">
                {template.Email_Subject || 'Untitled Template'}
              </h3>
              {template.Preheader_Text && <p className="text-sm text-gray-500 mb-4 truncate" data-id="element-4069">
                  {template.Preheader_Text}
                </p>}
              <div className="flex items-center gap-2 pt-4 border-t border-gray-100 mt-auto flex-wrap" data-id="element-4070">
                <Link to={`/templates/${template.ID}`} className="text-sm font-medium text-blue-600 hover:text-blue-800 flex items-center gap-1 whitespace-nowrap" data-id="element-4071">
                  <Edit2 className="h-3 w-3" data-id="element-4072" /> Edit
                </Link>
                <span className="text-gray-300" data-id="element-4073">|</span>
                <button onClick={() => setPreviewTemplate(template)} className="text-sm font-medium text-gray-500 hover:text-gray-700 flex items-center gap-1 whitespace-nowrap" data-id="element-4074">
                  <Eye className="h-3 w-3" data-id="element-4075" /> Preview
                </button>
                <span className="text-gray-300" data-id="element-4076">|</span>
                <button onClick={() => handleSaveAsTemplate(template.ID)} disabled={savingAsTemplate === template.ID} className="text-sm font-medium text-amber-600 hover:text-amber-800 flex items-center gap-1 whitespace-nowrap disabled:opacity-50" data-id="element-4077">
                  {savingAsTemplate === template.ID ? <Loader2 className="h-3 w-3 animate-spin" data-id="element-4078" /> : <BookmarkPlusIcon className="h-3 w-3" data-id="element-4079" />}
                  Save as Template
                </button>
                <span className="text-gray-300" data-id="element-4080">|</span>
                <button onClick={() => setDeleteConfirm(template.ID)} className="text-sm font-medium text-red-500 hover:text-red-700 flex items-center gap-1 whitespace-nowrap" data-id="element-4081">
                  <Trash2 className="h-3 w-3" data-id="element-4082" /> Delete
                </button>
              </div>
            </div>)}
        </div>}

      {/* Preview Modal */}
      <PreviewModal isOpen={!!previewTemplate} onClose={() => setPreviewTemplate(null)} title={`Preview: ${previewTemplate?.Email_Subject}`} data-id="element-4083">
        {previewTemplate && <EmailPreview subject={previewTemplate.Email_Subject} html={previewTemplate.Email_HTML_Body} data-id="element-4084" />}
      </PreviewModal>

      {/* Delete Confirmation */}
      {deleteConfirm && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-4085">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-4086">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setDeleteConfirm(null)} data-id="element-4087" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-id="element-4088">
              <div className="flex items-center gap-4" data-id="element-4089">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center" data-id="element-4090">
                  <Trash2 className="h-6 w-6 text-red-600" data-id="element-4091" />
                </div>
                <div data-id="element-4092">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-4093">
                    Delete Email Template
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-4094">
                    Are you sure? This will permanently remove the template from
                    Zoho Creator.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6" data-id="element-4095">
                <button onClick={() => setDeleteConfirm(null)} disabled={isDeleting} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-4096">
                  Cancel
                </button>
                <button onClick={() => handleDelete(deleteConfirm)} disabled={isDeleting} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2" data-id="element-4097">
                  {isDeleting && <Loader2 className="h-4 w-4 animate-spin" data-id="element-4098" />}
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}